import { mixed, type AnySchema } from 'yup';
import {
    createDefaultStaticTableSettings,
    type TableBinding,
    type HorizontalAlignment,
    type TableRowSettings,
    type TableSettings,
} from './contracts';
import { getBindingCatalog } from '../../runtime/dataServices/bindingCatalog';
import {
    createDefaultTableLayoutFromBinding,
    getTableRuntimeBindingDescriptorByIdOrThrow,
} from './runtimeBinding';
import {
    assertNoUnknownKeys,
    normalizeOptionalString,
    parseOptionalRuntimeBinding,
} from '../runtimeBindingSettingsYupUtils';
import { isRecord } from '../../../../../utils/typeGuards';

const TABLE_ROOT_ALLOWED_KEYS = [
    'title',
    'showWhenEmpty',
    'spacious',
    'header',
    'rows',
    'columnAlignments',
    'columnWidths',
    'binding',
] as const;

const TABLE_ROW_ALLOWED_KEYS = ['cells'] as const;

function parseStringArray(value: unknown, path: string): string[] {
    if (!Array.isArray(value)) {
        throw new Error(`Expected "${path}" to be an array.`);
    }

    return value.map((entry, index) => {
        if (typeof entry !== 'string') {
            throw new Error(`Expected "${path}[${index}]" to be a string.`);
        }

        const trimmed = entry.trim();
        if (!trimmed.length) {
            throw new Error(`Expected "${path}[${index}]" to be a non-empty string.`);
        }

        return trimmed;
    });
}

function parseOptionalSizedStringArray(
    value: unknown,
    path: string,
    expectedLength: number
): string[] | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    const entries = parseStringArray(value, path);
    if (!entries.length) {
        return undefined;
    }

    if (entries.length !== expectedLength) {
        throw new Error(`Expected "${path}" to contain exactly ${expectedLength} values.`);
    }

    return entries;
}

function parseColumnAlignment(value: unknown, path: string): HorizontalAlignment {
    if (value !== 'left' && value !== 'center' && value !== 'right') {
        throw new Error(`Expected "${path}" to be one of: left, center, right.`);
    }

    return value;
}

function parseColumnAlignments(
    value: unknown,
    headerColumnCount: number
): HorizontalAlignment[] | undefined {
    const alignments = parseOptionalSizedStringArray(
        value,
        'table.columnAlignments',
        headerColumnCount
    );
    if (!alignments) {
        return undefined;
    }

    return alignments.map((entry, index) =>
        parseColumnAlignment(entry, `table.columnAlignments[${index}]`)
    );
}

function parseColumnWidths(value: unknown, headerColumnCount: number): string[] | undefined {
    return parseOptionalSizedStringArray(value, 'table.columnWidths', headerColumnCount);
}

function parseRows(value: unknown, headerColumnCount: number): TableRowSettings[] {
    if (!Array.isArray(value)) {
        throw new Error('Expected "table.rows" to be an array.');
    }

    return value.map((entry, rowIndex) => {
        if (!isRecord(entry)) {
            throw new Error(`Expected "table.rows[${rowIndex}]" to be an object.`);
        }

        assertNoUnknownKeys(entry, TABLE_ROW_ALLOWED_KEYS, `table.rows[${rowIndex}]`);
        const cells = parseStringArray(entry.cells, `table.rows[${rowIndex}].cells`);

        if (cells.length !== headerColumnCount) {
            throw new Error(
                `Expected "table.rows[${rowIndex}].cells" to contain exactly ${headerColumnCount} values.`
            );
        }

        return { cells };
    });
}

function parseBinding(value: unknown): TableBinding | null {
    return parseOptionalRuntimeBinding<TableBinding>({
        value,
        bindingPath: 'table.binding',
        resolveBindingById: (bindingId, arg) =>
            getBindingCatalog().toActiveDataBinding(
                getTableRuntimeBindingDescriptorByIdOrThrow(bindingId),
                arg
            ),
    });
}

function parseTitle(value: unknown): string | undefined {
    return normalizeOptionalString(value, 'table.title');
}

function parseSpacious(value: unknown): boolean | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (typeof value !== 'boolean') {
        throw new Error('Expected "table.spacious" to be a boolean.');
    }

    return value;
}

export function normalizeTableSettings(rawSettings: unknown): TableSettings {
    if (!isRecord(rawSettings)) {
        throw new Error('Expected "table" settings to be an object.');
    }

    assertNoUnknownKeys(rawSettings, TABLE_ROOT_ALLOWED_KEYS, 'table');
    if (!Object.hasOwn(rawSettings, 'binding')) {
        throw new Error('Expected "table.binding" to be explicitly defined.');
    }

    const { showWhenEmpty } = rawSettings;
    if (typeof showWhenEmpty !== 'undefined' && typeof showWhenEmpty !== 'boolean') {
        throw new Error('Expected "table.showWhenEmpty" to be a boolean.');
    }

    const binding = parseBinding(rawSettings.binding);
    const defaults =
        binding === null
            ? createDefaultStaticTableSettings()
            : createDefaultTableLayoutFromBinding(binding);

    const title = parseTitle(rawSettings.title) ?? defaults.title;
    const spacious = parseSpacious(rawSettings.spacious);

    if (binding !== null) {
        return {
            showWhenEmpty: showWhenEmpty ?? false,
            title,
            header: [...defaults.header],
            rows: [],
            ...(typeof defaults.columnAlignments === 'undefined'
                ? {}
                : { columnAlignments: [...defaults.columnAlignments] }),
            ...(typeof spacious === 'undefined' ? {} : { spacious }),
            binding,
        };
    }

    const header =
        typeof rawSettings.header === 'undefined'
            ? [...defaults.header]
            : parseStringArray(rawSettings.header, 'table.header');
    if (!header.length) {
        throw new Error('Expected "table.header" to contain at least one value.');
    }

    const rows = parseRows(
        typeof rawSettings.rows === 'undefined' ? defaults.rows : rawSettings.rows,
        header.length
    );

    const columnAlignments =
        typeof rawSettings.columnAlignments === 'undefined'
            ? defaults.columnAlignments
            : parseColumnAlignments(rawSettings.columnAlignments, header.length);
    if (columnAlignments && columnAlignments.length !== header.length) {
        throw new Error(
            `Expected "table.columnAlignments" to contain exactly ${header.length} values.`
        );
    }

    const columnWidths = parseColumnWidths(rawSettings.columnWidths, header.length);

    return {
        showWhenEmpty: showWhenEmpty ?? false,
        title,
        header,
        rows,
        ...(typeof columnAlignments === 'undefined' ? {} : { columnAlignments }),
        ...(typeof columnWidths === 'undefined' ? {} : { columnWidths }),
        ...(typeof spacious === 'undefined' ? {} : { spacious }),
        binding,
    };
}

export function buildTableSettingsYupSchema(): AnySchema {
    return mixed()
        .required('Expected "table" settings to be an object.')
        .test('table-settings-shape', 'Invalid table settings.', function test(value) {
            if (!isRecord(value)) {
                return this.createError({
                    message: 'Expected "table" settings to be an object.',
                });
            }

            try {
                normalizeTableSettings(value);
                return true;
            } catch (error) {
                const normalizedError = error instanceof Error ? error : new Error(String(error));
                return this.createError({ message: normalizedError.message });
            }
        });
}
